import type { WorkspaceApp } from "@bafe/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../lib/api.js";
import { presentError } from "../lib/errors.js";
import { useSession } from "../lib/session.js";
import { APP_OPTIONS } from "../lib/workspace-apps.js";

export function WorkspaceAppsPanel({
	organisationId,
	organisationName,
	canManage,
}: {
	organisationId: string;
	organisationName: string;
	canManage: boolean;
}) {
	const queryClient = useQueryClient();
	const session = useSession();
	const [selected, setSelected] = useState<WorkspaceApp[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [ready, setReady] = useState(false);

	const membership = session.data?.memberships.find((item) => item.organisationId === organisationId);
	const manage =
		canManage ||
		session.data?.platformOwner ||
		membership?.role === "owner" ||
		membership?.role === "admin";

	useEffect(() => {
		let cancelled = false;
		setReady(false);
		setError(null);
		void (async () => {
			try {
				await api.selectWorkspace(organisationId);
				if (!cancelled) setReady(true);
			} catch (caught) {
				if (!cancelled) setError(presentError(caught).title);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [organisationId]);

	const apps = useQuery({
		queryKey: ["workspace-apps", organisationId],
		queryFn: () => api.getWorkspaceApps(),
		enabled: ready,
	});

	useEffect(() => {
		if (apps.data) setSelected(apps.data.apps);
	}, [apps.data]);

	const save = useMutation({
		mutationFn: () => api.setWorkspaceApps(selected),
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
		onError: (caught) => setError(presentError(caught).title),
	});

	function toggle(app: WorkspaceApp) {
		setSelected((current) =>
			current.includes(app) ? current.filter((item) => item !== app) : [...current, app],
		);
	}

	if (error) return <p className="err">{error}</p>;
	if (!ready || apps.isPending) return <p className="empty">Loading apps.</p>;
	if (apps.isError) return <p className="err">{presentError(apps.error).title}</p>;

	return (
		<div>
			<p className="sub">{organisationName}. Owners and admins can turn apps on for this workspace.</p>
			<div className="card">
				<div className="checks">
					{APP_OPTIONS.map((option) => (
						<label key={option.id} className="check">
							<input
								type="checkbox"
								checked={selected.includes(option.id)}
								disabled={!manage}
								onChange={() => toggle(option.id)}
							/>
							<span>
								{option.label}
								<span className="quiet"> — {option.detail}</span>
							</span>
						</label>
					))}
				</div>
				{manage && (
					<button
						className="btn"
						type="button"
						style={{ marginTop: 14 }}
						disabled={save.isPending}
						onClick={() => save.mutate()}
					>
						{save.isPending ? "Saving" : "Save apps"}
					</button>
				)}
			</div>
		</div>
	);
}
